/**
 * Owns the agent-run-notes state shown when the Agent sidebar tab is
 * active: the hydrated notes, the change handler from <AgentExplorer>,
 * the click-to-focus handler that drives map zoom, and a clear action
 * called by the sidebar-tab-transition cleanup effect.
 */

import * as geojson from 'geojson';
import React from 'react';

import {ApiNote} from 'app/modules/Remote/Feature';
import {NotesActions, StateApiNote} from 'app/stores/NotesStore';
import {NOTE_TYPE_AGENT} from 'app/utils/constants';
import {useRefWithDeps} from 'app/utils/hookUtils';
import * as noteUtils from 'app/utils/noteUtils';

interface Args {
  notesActions: NotesActions;
  zoomToGeometry: (geometry: geojson.GeoJSON | null) => void;
  focusedNoteId: number | string | null;
}

/** All a click needs of a note, so a caller holding less than a full note can focus one. */
export type ClickableNote = Pick<ApiNote, 'id' | 'geometry' | 'noteType'>;

export interface AgentRunNotesState {
  agentRunNotes: StateApiNote[];
  handleAgentRunNotesChange: (notes: ApiNote[], runId: number | null) => void;
  handleAgentNoteClick: (note: ClickableNote) => void;
  handleShowAllObservations: () => void;
  handleClearFocus: () => void;
  clearAgentRunNotes: () => void;
}

export default function useAgentRunNotes({
  notesActions,
  zoomToGeometry,
  focusedNoteId,
}: Args): AgentRunNotesState {
  const [agentRunNotes, setAgentRunNotes] = React.useState<StateApiNote[]>([]);

  // The run we've already fit the camera to. We fit once per run open so the
  // camera doesn't jump as observations stream in during an in-progress run.
  const fittedRunIdRef = React.useRef<number | null>(null);

  const focusedNoteIdRef = useRefWithDeps(focusedNoteId, [focusedNoteId]);

  const fitToObservations = React.useCallback(
    (notes: StateApiNote[]) => {
      const featureCollection = noteUtils.notesToFeatureCollection(notes);
      if (featureCollection.features.length === 0) {
        return false;
      }
      zoomToGeometry(featureCollection);
      return true;
    },
    [zoomToGeometry]
  );

  const handleAgentRunNotesChange = React.useCallback(
    (notes: ApiNote[], runId: number | null) => {
      if (notes.length === 0) {
        notesActions.focusNote(null);
      }
      const hydratedNotes = notes.map((note) => notesActions.hydrateNote(note));
      setAgentRunNotes(hydratedNotes);
      notesActions.loadNotes();

      if (runId === null) {
        fittedRunIdRef.current = null;
        return;
      }

      if (fittedRunIdRef.current !== runId) {
        // A deep-linked note already zooms the map to itself; only fit to all
        // observations when opening a run without one.
        if (focusedNoteIdRef.current) {
          fittedRunIdRef.current = runId;
        } else if (fitToObservations(hydratedNotes)) {
          fittedRunIdRef.current = runId;
        }
      }
    },
    [notesActions, fitToObservations, focusedNoteIdRef]
  );

  const handleAgentNoteClick = React.useCallback(
    (note: ClickableNote) => {
      // only "focus note" (eg, put it in the URL) if it's an agent note.
      // otherwise, we are unsure whether to take the user to the notes tab
      // or to the agent tab since user notes live in both places.
      if (note.noteType === NOTE_TYPE_AGENT) notesActions.focusNote(note.id);
      zoomToGeometry(note.geometry);
    },
    [notesActions, zoomToGeometry]
  );

  const handleShowAllObservations = React.useCallback(() => {
    notesActions.focusNote(null);
    fitToObservations(agentRunNotes);
  }, [notesActions, fitToObservations, agentRunNotes]);

  const handleClearFocus = React.useCallback(() => {
    notesActions.focusNote(null);
  }, [notesActions]);

  const clearAgentRunNotes = React.useCallback(() => {
    setAgentRunNotes([]);
    fittedRunIdRef.current = null;
  }, []);

  return {
    agentRunNotes,
    handleAgentRunNotesChange,
    handleAgentNoteClick,
    handleShowAllObservations,
    handleClearFocus,
    clearAgentRunNotes,
  };
}
